import { useEffect } from "react";
import Grid from "@mui/material/Grid";
import CardContent from "@mui/material/CardContent";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { Link } from 'react-router-dom';
import Button from '@mui/material/Button';
import useMediaQuery from '@mui/material/useMediaQuery';
import Card from "@mui/material/Card";

// Icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import InsightsIcon from '@mui/icons-material/Insights';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import SecurityIcon from '@mui/icons-material/Security';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EmailIcon from '@mui/icons-material/Email';
import StorageIcon from '@mui/icons-material/Storage';
import PeopleIcon from '@mui/icons-material/People';
import WarningIcon from '@mui/icons-material/Warning';
import ShieldIcon from '@mui/icons-material/Shield';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import BarChartIcon from '@mui/icons-material/BarChart';
import PricingCoffee from './Components/PricingCoffee';

// Images
import noc from 'assets/images/photos/noc.jpg';
import ms365Graphs from 'assets/images/platform/ms365_graphs.png';
import weakAlert from 'assets/images/illustrations/Weak-alert-example.png';
import decisionTree from 'assets/images/illustrations/decision-tree.png';

export default function SMBs() {
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

    useEffect(() => {
        document.title = 'PREVAIL for Small & Medium Businesses';
        window.scrollTo(0, 0);
    });

    return (
        <>
            {/* ─── HERO ─── */}
            <Box
                width="100%"
                sx={{
                    minHeight: isMobile ? '100vh' : '72vh',
                    background: 'linear-gradient(160deg, #000000 0%, #1a0000 50%, #242331 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={2} p={3} mt={8} justifyContent="center" alignItems="center">
                    <Grid item xs={12} md={7} p={2}>
                        <Typography
                            sx={{ typography: { xs: 'h4', md: 'h2' }, mb: 2 }}
                            fontWeight="bold"
                            color="grey.100"
                        >
                            Enterprise Security.<br />Without the Enterprise Price.
                        </Typography>
                        <Typography
                            sx={{ typography: { xs: 'h6', md: 'h5' }, mb: 1 }}
                            color="prevailRed.main"
                            fontWeight="bold"
                        >
                            AI-Powered Protection for Your Business. Starting at $2/User/Month.
                        </Typography>
                        <Typography
                            variant="h6"
                            color="grey.400"
                            sx={{ mb: 4, mt: 2, lineHeight: 1.8 }}
                        >
                            Small businesses are the #1 target for cybercriminals. You don't need a full
                            security team to fight back. PREVAIL monitors your Microsoft 365 around the
                            clock — detecting breaches in under 1 minute, automatically.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Link to="https://app.prevailcyber.com/sign-up" target="_blank">
                                <Button
                                    variant="contained"
                                    color="prevailRed"
                                    size="large"
                                    sx={{ pl: 5, pr: 5, py: 1.5 }}
                                >
                                    <Typography sx={{ fontSize: '1.1em' }} color="grey.100">
                                        Start Free Trial
                                    </Typography>
                                </Button>
                            </Link>
                            <Link to="/#contact">
                                <Button
                                    variant="outlined"
                                    size="large"
                                    sx={{ pl: 4, pr: 4, py: 1.5, borderColor: 'grey.600', color: 'grey.300' }}
                                >
                                    <Typography sx={{ fontSize: '1.1em' }}>Talk to Us</Typography>
                                </Button>
                            </Link>
                        </Box>
                    </Grid>
                   
                </Grid>
            </Box>

            {/* ─── STATS BAR ─── */}
            <Box sx={{ background: '#6b0000', py: 4 }}>
                <Grid container maxWidth="xl" spacing={2} justifyContent="center" alignItems="center"
                    textAlign="center" sx={{ mx: 'auto' }}>
                    {[
                        { stat: '43%', label: 'Of cyberattacks target small businesses' },
                        { stat: '$4.45M', label: 'Average cost of a data breach' },
                        { stat: '60%', label: 'Of SMBs close within 6 months of a breach' },
                        { stat: '$2', label: 'Per user/month for full protection' },
                    ].map(({ stat, label }) => (
                        <Grid item size={{ xs: 6, md: 3 }} key={stat}>
                            <Typography variant="h4" color="grey.100" fontWeight="bold">{stat}</Typography>
                            <Typography variant="body2" color="grey.400" mt={0.5}>{label}</Typography>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* ─── WHY SMBS ARE AT RISK ─── */}
            <Box
                sx={{
                    background: '#242331',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={2} justifyContent="center">
                    <Grid item xs={12} textAlign="center" mb={4}>
                        <Typography variant="h3" color="grey.100" fontWeight="bold">
                            Small Business. Big Target.
                        </Typography>
                        <Typography variant="h6" color="grey.400" mt={2} sx={{ maxWidth: '720px', mx: 'auto' }}>
                            Attackers know that small businesses have valuable data but fewer defenses
                            than large corporations. Your Microsoft 365 account — your email, files,
                            and Teams — is the easiest door in.
                        </Typography>
                    </Grid>

                    {[
                        {
                            icon: <EmailIcon sx={{ fontSize: 48 }} />,
                            title: 'Phishing is Everywhere',
                            description:
                                "It only takes one employee clicking the wrong link to hand over your credentials. Attackers send convincing emails that look exactly like Microsoft, your bank, or a vendor. Once they're in your account, they're invisible.",
                        },
                        {
                            icon: <StorageIcon sx={{ fontSize: 48 }} />,
                            title: 'Your Data Has Value',
                            description:
                                'Client records, financial data, contracts, and intellectual property — attackers will sell it, ransom it, or use it against you. A single SharePoint leak can expose years of work.',
                        },
                        {
                            icon: <PeopleIcon sx={{ fontSize: 48 }} />,
                            title: 'Threats Come from Inside Too',
                            description:
                                "A disgruntled employee or someone about to quit can take your client list, project files, or proprietary data with them. You won't notice until it's too late — unless you have PREVAIL.",
                        },
                    ].map(({ icon, title, description }) => (
                        <Grid item xs={12} md={4} key={title} p={1}>
                            <Card
                                sx={{
                                    background: '#1a1929',
                                    border: '1px solid rgba(217,0,0,0.35)',
                                    borderRadius: 2,
                                    height: '100%',
                                }}
                            >
                                <CardContent sx={{ p: 4 }}>
                                    <Typography color="prevailRed.main" mb={2}>{icon}</Typography>
                                    <Typography variant="h5" color="grey.100" fontWeight="bold" mb={2}>
                                        {title}
                                    </Typography>
                                    <Typography variant="body2" color="grey.400" lineHeight={1.8}>
                                        {description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* ─── WHAT A BREACH COSTS YOU ─── */}
            <Box
                sx={{
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={4} justifyContent="center">
                    <Grid item size={{ xs: 12 }} textAlign="center" mb={2}>
                        <Typography variant="h3" color="grey.800" fontWeight="bold">
                            The Real Cost of Doing Nothing
                        </Typography>
                        <Typography variant="h6" color="grey.600" mt={2} sx={{ maxWidth: '650px', mx: 'auto' }}>
                            A breach isn't just an IT problem. For a small business, it can be existential.
                        </Typography>
                    </Grid>

                    {[
                        {
                            icon: <AttachMoneyIcon sx={{ fontSize: 40 }} />,
                            stat: '$4.45M',
                            label: 'Average breach cost',
                            detail: 'Even for small businesses, a data breach can mean legal fees, regulatory fines, and lost business that far exceed what you might imagine.',
                        },
                        {
                            icon: <AccessTimeIcon sx={{ fontSize: 40 }} />,
                            stat: '328 Days',
                            label: 'Average time to detect',
                            detail: 'Without behavioral detection, attackers often live inside your systems for nearly a year before anyone notices — quietly reading emails and stealing files.',
                        },
                        {
                            icon: <WarningIcon sx={{ fontSize: 40 }} />,
                            stat: '60%',
                            label: 'Close within 6 months',
                            detail: 'Of small businesses that suffer a major cyberattack do not survive 6 months. The combination of downtime, reputation damage, and recovery costs is often fatal.',
                        },
                    ].map(({ icon, stat, label, detail }) => (
                        <Grid item size={{ xs: 12, md: 4 }} key={stat}>
                            <Box
                                sx={{
                                    background: '#242331',
                                    borderRadius: 2,
                                    p: 4,
                                    height: '100%',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                }}
                            >
                                <Typography color="prevailRed.main" mb={1}>{icon}</Typography>
                                <Typography variant="h3" color="grey.200" fontWeight="bold" mb={0.5}>{stat}</Typography>
                                <Typography variant="body2" color="grey.400" fontWeight="bold" mb={2}>{label}</Typography>
                                <Typography variant="body2" color="grey.400" lineHeight={1.8}>{detail}</Typography>
                            </Box>
                        </Grid>
                    ))}

                    <Grid item xs={12} textAlign="center" mt={2}>
                        <Typography variant="caption" color="grey.600" sx={{ fontStyle: 'italic' }}>
                            * Sources: IBM Security Cost of a Data Breach Report; U.S. National Cyber Security Alliance
                        </Typography>
                    </Grid>
                </Grid>
            </Box>

            {/* ─── HOW PREVAIL WORKS ─── */}
            <Box
                sx={{
                    background: '#ffffff',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={2} justifyContent="center">
                    <Grid item xs={12} textAlign="center" mb={6}>
                        <Typography
                            variant="overline"
                            color="prevailRed.main"
                            fontWeight="bold"
                            sx={{ letterSpacing: 3, fontSize: '0.9em' }}
                        >
                            HOW IT WORKS
                        </Typography>
                        <Typography variant="h3" color="grey.800" fontWeight="bold" mt={1}>
                            Set Up in 3 Minutes. Protected Forever.
                        </Typography>
                        <Typography variant="h6" color="grey.600" mt={2} sx={{ maxWidth: '650px', mx: 'auto' }}>
                            No IT team. No complex configuration. No expertise required.
                            PREVAIL is designed so any business owner can get protected in minutes.
                        </Typography>
                    </Grid>

                    {[
                        {
                            icon: <CloudDownloadIcon sx={{ fontSize: 56 }} />,
                            step: '01',
                            title: 'Connect Your Microsoft 365',
                            description:
                                'Create an account and add your Microsoft 365 tenant by granting access to the PREVAIL Azure application. The whole process takes less than 3 minutes and requires no technical expertise.',
                        },
                        {
                            icon: <InsightsIcon sx={{ fontSize: 56 }} />,
                            step: '02',
                            title: 'AI Learns Your Business',
                            description:
                                'PREVAIL immediately starts collecting sign-in, activity, and file data from your Microsoft 365 environment. Our proprietary machine learning builds a unique behavioral profile of how your team normally works.',
                        },
                        {
                            icon: <NotificationImportantIcon sx={{ fontSize: 56 }} />,
                            step: '03',
                            title: 'Get Alerted Instantly',
                            description:
                                "The moment something looks wrong — a stolen credential, a suspicious login, or unusual file access — you receive an alert. In under 1 minute. Before damage is done.",
                        },
                    ].map(({ icon, step, title, description }) => (
                        <Grid item xs={12} md={4} key={step} p={2}>
                            <Box
                                sx={{
                                    border: '2px solid',
                                    borderColor: 'grey.200',
                                    borderRadius: 2,
                                    p: 4,
                                    height: '100%',
                                    position: 'relative',
                                    transition: 'border-color 0.3s',
                                    '&:hover': { borderColor: '#d90000' },
                                }}
                            >
                                <Typography
                                    sx={{
                                        position: 'absolute',
                                        top: 12,
                                        right: 18,
                                        fontSize: '3.5em',
                                        fontWeight: 900,
                                        color: 'rgba(217,0,0,0.07)',
                                        lineHeight: 1,
                                        userSelect: 'none',
                                    }}
                                >
                                    {step}
                                </Typography>
                                <Typography color="primary.red" mb={2}>{icon}</Typography>
                                <Typography variant="h5" color="grey.800" fontWeight="bold" mb={2}>{title}</Typography>
                                <Typography variant="body2" color="grey.600" lineHeight={1.8} sx={{ fontSize: '1em' }}>
                                    {description}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* ─── PLATFORM VIEW ─── */}
            <Box
                sx={{
                    background: '#242331',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={6} justifyContent="center" alignItems="center">
                    <Grid item xs={12} textAlign="center" mb={2}>
                        <Typography
                            variant="overline"
                            color="prevailRed.main"
                            fontWeight="bold"
                            sx={{ letterSpacing: 3, fontSize: '0.9em' }}
                        >
                            THE PLATFORM
                        </Typography>
                        <Typography variant="h3" color="grey.100" fontWeight="bold" mt={1}>
                            See Everything. Do Nothing Extra.
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
                        <Typography variant="h5" color="grey.200" fontWeight="bold" mb={3}>
                            Your Security Dashboard — In Plain English
                        </Typography>
                        <Typography variant="body1" color="grey.400" mb={3} lineHeight={1.8} sx={{ fontSize: '1.05em' }}>
                            PREVAIL shows you exactly what's happening across your business in a clean,
                            simple dashboard. No security jargon. No confusing charts. Just the information
                            you need, when you need it.
                        </Typography>
                        {[
                            { icon: <ShieldIcon sx={{ fontSize: 20, color: '#d90000' }} />, text: 'Instant alerts when accounts are compromised' },
                            { icon: <BarChartIcon sx={{ fontSize: 20, color: '#d90000' }} />, text: 'User activity reports — see who is doing what with your data' },
                            { icon: <PhoneAndroidIcon sx={{ fontSize: 20, color: '#d90000' }} />, text: 'Notifications by email so you\'re always in the loop' },
                            { icon: <SecurityIcon sx={{ fontSize: 20, color: '#d90000' }} />, text: 'Enable or disable alert types with a single click' },
                            { icon: <StorageIcon sx={{ fontSize: 20, color: '#d90000' }} />, text: 'Historical data so you can prove what happened and when' },
                        ].map(({ icon, text }) => (
                            <Box key={text} display="flex" alignItems="flex-start" gap={1.5} mb={2}>
                                {icon}
                                <Typography variant="body2" color="grey.300" sx={{ fontSize: '1em' }}>{text}</Typography>
                            </Box>
                        ))}
                    </Grid>
                   
                </Grid>

                <Grid container maxWidth="lg" spacing={6} justifyContent="center" alignItems="center" mt={8}>
                    
                    <Grid item size={{xs: 12, md: 6}} >
                        <Typography variant="h5" color="grey.200" fontWeight="bold" mb={3}>
                            Know Before Damage Is Done
                        </Typography>
                        <Typography variant="body1" color="grey.400" mb={3} lineHeight={1.8} sx={{ fontSize: '1.05em' }}>
                            Traditional security tools generate hundreds of alerts a day, most of which
                            are false alarms. PREVAIL's AI filters the noise and only alerts you when
                            something real is happening.
                        </Typography>
                        <Typography variant="body1" color="grey.400" lineHeight={1.8} sx={{ fontSize: '1.05em' }}>
                            When you receive a PREVAIL alert, it means an active threat has been detected.
                            The alert tells you exactly what happened, who is affected, and what to do next —
                            so you can act immediately, even without a security background.
                        </Typography>
                    </Grid>
                </Grid>
            </Box>

            {/* ─── NO SECURITY TEAM NEEDED ─── */}
            <Box
                sx={{
                    background: '#f8f9fa',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={4} justifyContent="center">
                    <Grid item xs={12} textAlign="center" mb={4}>
                        <Typography variant="h3" color="grey.800" fontWeight="bold">
                            With PREVAIL vs. Without
                        </Typography>
                        <Typography variant="h6" color="grey.600" mt={2} sx={{ maxWidth: '650px', mx: 'auto' }}>
                            You don't need to hire a security expert. PREVAIL does the monitoring
                            automatically — all day, every day.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Card sx={{ border: '2px solid #e0e0e0', borderRadius: 2, height: '100%' }}>
                            <CardContent sx={{ p: 4 }}>
                                <Typography variant="h5" color="grey.700" fontWeight="bold" mb={3}>
                                    Without PREVAIL
                                </Typography>
                                {[
                                    'No visibility into who is accessing your files and data',
                                    'Breaches go undetected for months on average',
                                    'You find out about attacks from ransom notes or clients',
                                    'Microsoft\'s built-in security requires IT expertise to configure',
                                    'No way to prove if a departing employee stole your data',
                                    'A single phishing click can compromise your entire business',
                                ].map((point) => (
                                    <Box key={point} display="flex" alignItems="flex-start" gap={1.5} mb={2}>
                                        <CancelIcon sx={{ color: '#d90000', mt: 0.2, fontSize: 20, flexShrink: 0 }} />
                                        <Typography variant="body2" color="grey.600" sx={{ fontSize: '0.95em' }}>{point}</Typography>
                                    </Box>
                                ))}
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Card sx={{ border: '2px solid #d90000', borderRadius: 2, height: '100%' }}>
                            <CardContent sx={{ p: 4 }}>
                                <Typography variant="h5" color="prevailRed.main" fontWeight="bold" mb={3}>
                                    With PREVAIL
                                </Typography>
                                {[
                                    'Complete visibility into all user activity across Microsoft 365',
                                    'Active threats detected and alerted in under 1 minute',
                                    'You know before the attacker can do damage',
                                    'Connected and protecting in under 3 minutes — no IT needed',
                                    'Proof of insider activity for legal or HR purposes',
                                    'AI adapts to new attack methods automatically',
                                ].map((point) => (
                                    <Box key={point} display="flex" alignItems="flex-start" gap={1.5} mb={2}>
                                        <CheckCircleIcon sx={{ color: '#47A025', mt: 0.2, fontSize: 20, flexShrink: 0 }} />
                                        <Typography variant="body2" color="grey.700" sx={{ fontSize: '0.95em' }}>{point}</Typography>
                                    </Box>
                                ))}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

         

            {/* ─── PRICING ─── */}
            <PricingCoffee />
           {/* } <Box
                sx={{
                    background: '#ffffff',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={4} justifyContent="center" textAlign="center">
                    <Grid item xs={12}>
                        <Typography variant="h3" color="grey.800" fontWeight="bold">
                            Less Than a Cup of Coffee Per User
                        </Typography>
                        <Typography variant="h6" color="grey.600" mt={2} mb={2} sx={{ maxWidth: '600px', mx: 'auto' }}>
                            For less than $2 per user per month, you get 24/7 AI-powered threat detection
                            across your entire Microsoft 365 environment. No hidden fees. No contracts required.
                        </Typography>
                    </Grid>

                    {[
                        {
                            plan: 'Long Term Storage',
                            price: '$4',
                            period: '/user/month',
                            badge: null,
                            features: [
                                'Complete Microsoft 365 protection',
                                '1 year data & log storage',
                                '3 year trend storage',
                                'Up to 5 admin users',
                                'Email support',
                            ],
                            highlight: false,
                            cta: 'Start Free Trial',
                            link: 'https://app.prevailcyber.com/sign-up',
                            external: true,
                        },
                        {
                            plan: 'Standard',
                            price: '$2',
                            period: '/user/month',
                            badge: 'MOST POPULAR',
                            features: [
                                'Everything in Standard',
                                '3 year data & log storage',
                                '5 year trend storage',
                                'Up to 10 admin users',
                                'Email & phone support',
                            ],
                            highlight: true,
                            cta: 'Start Free Trial',
                            link: 'https://app.prevailcyber.com/sign-up',
                            external: true,
                        },
                        {
                            plan: 'Enterprise',
                            price: '$5',
                            period: '/user/month',
                            badge: null,
                            features: [
                                'All services included',
                                '7 year data & log storage',
                                'Unlimited admin users',
                                'Custom reporting',
                                'Early access to new features',
                            ],
                            highlight: false,
                            cta: 'Contact Us',
                            link: '/contact-us',
                            external: false,
                        },
                    ].map(({ plan, price, period, badge, features, highlight, cta, link, external }) => (
                        <Grid item size={{ xs: 12, md: 4 }} key={plan}>
                            <Card
                                sx={{
                                    border: highlight ? '2px solid #d90000' : '2px solid #e0e0e0',
                                    borderRadius: 2,
                                    height: '100%',
                                    boxShadow: highlight ? '0 8px 24px rgba(217,0,0,0.12)' : 'none',
                                }}
                            >
                                <CardContent sx={{ p: 4 }}>
                                    {badge && (
                                        <Box
                                            sx={{
                                                background: '#d90000',
                                                color: 'white',
                                                borderRadius: 1,
                                                px: 1.5,
                                                py: 0.5,
                                                display: 'inline-block',
                                                mb: 1,
                                            }}
                                        >
                                            <Typography variant="caption" color="grey.100" fontWeight="bold">{badge}</Typography>
                                        </Box>
                                    )}
                                    <Typography
                                        variant="h6"
                                        color={highlight ? 'prevailRed.main' : 'grey.700'}
                                        fontWeight="bold"
                                        mb={1}
                                        mt={badge ? 0 : 3.5}
                                    >
                                        {plan}
                                    </Typography>
                                    <Box display="flex" alignItems="baseline" justifyContent="center" mb={1}>
                                        <Typography variant="h3" color="grey.800" fontWeight="bold">{price}</Typography>
                                        <Typography variant="body2" color="grey.500" ml={0.5}>{period}</Typography>
                                    </Box>
                                    <Divider sx={{ my: 2 }} />
                                    {features.map((f) => (
                                        <Box key={f} display="flex" alignItems="center" gap={1} mb={1.5} textAlign="left">
                                            <CheckCircleIcon sx={{ color: '#47A025', fontSize: 18, flexShrink: 0 }} />
                                            <Typography variant="body2" color="grey.600">{f}</Typography>
                                        </Box>
                                    ))}
                                    <Box mt={3}>
                                        <Link to={link} target={external ? '_blank' : undefined}>
                                            <Button
                                                variant={highlight ? 'contained' : 'outlined'}
                                                color="prevailRed"
                                                fullWidth
                                            >
                                                <Typography color={highlight ? 'grey.100' : undefined}>{cta}</Typography>
                                            </Button>
                                        </Link>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}

                    <Grid item xs={12}>
                        <Typography variant="body2" color="grey.500" mt={2}>
                            Using a Managed IT Services Provider?{' '}
                            <Link to="/managed-partners" style={{ color: '#d90000', textDecoration: 'underline' }}>
                                Ask your MSP about PREVAIL →
                            </Link>
                        </Typography>
                    </Grid>
                </Grid>
            </Box> */}

            {/* ─── FINAL CTA ─── */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #2D0000 0%, #6b0000 60%, #242331 100%)',
                    py: 12,
                    display: 'grid',
                    placeItems: 'center',
                    textAlign: 'center',
                }}
            >
                <Grid container maxWidth="md" spacing={2} justifyContent="center">
                    <Grid item xs={12}>
                        <Typography variant="h3" color="grey.100" fontWeight="bold" mb={2}>
                            Don't Wait for a Breach to Find Out
                        </Typography>
                        <Typography variant="h6" color="grey.400" mb={5} sx={{ maxWidth: '580px', mx: 'auto' }}>
                            Connect your Microsoft 365 in under 3 minutes.
                            No IT team. No credit card. No excuses.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Link to="https://app.prevailcyber.com/sign-up" target="_blank">
                                <Button
                                    variant="contained"
                                    color="prevailRed"
                                    size="large"
                                    sx={{ pl: 6, pr: 6, py: 2 }}
                                >
                                    <Typography sx={{ fontSize: '1.2em' }} color="grey.100">
                                        Start Free Trial
                                    </Typography>
                                </Button>
                            </Link>
                            <Link to="/#contact">
                                <Button
                                    variant="outlined"
                                    size="large"
                                    sx={{ pl: 5, pr: 5, py: 2, borderColor: 'grey.500', color: 'grey.300' }}
                                >
                                    <Typography sx={{ fontSize: '1.2em' }}>Talk to Us</Typography>
                                </Button>
                            </Link>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
}
